
import React, { useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import XIcon from './icons/XIcon';
import ShareIcon from './icons/ShareIcon';

interface PrestigeModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalImageUrl: string;
  generatedImageUrl: string;
  smileRating: any;
  afterSmileRating: any;
}

const AnimatedCounter: React.FC<{ from: number; to: number; delay?: number; className?: string }> = ({ from, to, delay = 0, className }) => {
  const count = useMotionValue(from);
  const rounded = useTransform(count, (latest) => Math.round(latest));

  useEffect(() => {
    const controls = animate(count, to, {
      duration: 1.5,
      delay: delay,
      ease: 'easeOut',
    });
    return controls.stop;
  }, [from, to, delay]);

  return <motion.span className={className}>{rounded}</motion.span>;
};

const MetricBar: React.FC<{ label: string; beforeScore: number; afterScore: number, delay: number }> = ({ label, beforeScore, afterScore, delay }) => {
  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay + 1.2, ease: 'easeOut' }}
    >
      <div className="flex justify-between items-center mb-1 text-xs">
        <p className="font-semibold text-zinc-300">{label}</p>
        <p className="font-medium">
          <span className="text-zinc-400">{beforeScore}</span>
          <span className="text-amber-400/50 mx-1">&rarr;</span>
          <span className="font-bold text-amber-400">{afterScore}</span>
        </p>
      </div>
      <div className="relative h-2 w-full bg-zinc-700/50 rounded-full overflow-hidden">
        <motion.div
          className="absolute top-0 left-0 h-full bg-zinc-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${beforeScore}%` }}
          transition={{ duration: 1, delay: delay + 1.4, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-400 to-amber-300 rounded-full"
          initial={{ width: `${beforeScore}%` }}
          animate={{ width: `${afterScore}%` }}
          transition={{ duration: 1.2, delay: delay + 1.5, ease: 'easeInOut' }}
        />
      </div>
    </motion.div>
  );
};


const PrestigeModal: React.FC<PrestigeModalProps> = ({ isOpen, onClose, originalImageUrl, generatedImageUrl, smileRating, afterSmileRating }) => {

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleShare = async () => {
    const shareData = {
      title: 'FuturaSmile Transformation',
      text: 'Check out the incredible AI-powered smile transformation I created with FuturaSmile!',
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for desktop browsers
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Link copied to clipboard! Ready to share.');
      }
    } catch (err) {
      console.error('Share failed:', err);
      alert('Sharing failed. Please try again.');
    }
  };

  if (!smileRating || !afterSmileRating) return null;

  const scoreBefore = smileRating.overallSmileScore.score;
  const scoreAfter = afterSmileRating.overallSmileScore.score;
  const scoreChange = scoreAfter - scoreBefore;

  const transformationAnalysis = { // This is a mock structure based on your PDF report. Adjust if needed.
    comparativeMetrics: [
      { area: "Symmetry & Alignment", beforeScore: smileRating.detailedScores.symmetryAndAlignment, afterScore: afterSmileRating.detailedScores.symmetryAndAlignment },
      { area: "Color & Brightness", beforeScore: smileRating.detailedScores.colorAndBrightness, afterScore: afterSmileRating.detailedScores.colorAndBrightness },
      { area: "Shape & Proportions", beforeScore: smileRating.detailedScores.shapeAndProportions, afterScore: afterSmileRating.detailedScores.shapeAndProportions },
      { area: "Gum Health & Contour", beforeScore: smileRating.detailedScores.gumHealthAndContour, afterScore: afterSmileRating.detailedScores.gumHealthAndContour },
    ]
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <motion.div
            className="relative w-full max-w-md max-h-[90vh] bg-gradient-to-br from-[#1c1c1c] to-[#121212] rounded-2xl border border-amber-400/20 shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
            initial={{ scale: 0.95, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut', delay: 0.1 } }}
            exit={{ scale: 0.95, y: 20, opacity: 0, transition: { duration: 0.3, ease: 'easeIn' } }}
          >
            {/* Header */}
            <motion.header
              className="flex-shrink-0 p-4 flex justify-between items-center border-b border-amber-400/10"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
            >
              <h2 className="text-lg font-bold text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                Smile Transformation
              </h2>
              <button onClick={onClose} className="p-1.5 text-zinc-400 rounded-full hover:bg-white/10 transition-colors">
                <XIcon className="w-5 h-5" />
              </button>
            </motion.header>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">

              {/* Score Showcase */}
              <div className="flex justify-between items-center text-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
                >
                  <p className="text-xs font-semibold tracking-widest text-zinc-500">Before</p>
                  <AnimatedCounter from={0} to={scoreBefore} delay={0.6} className="text-4xl font-medium text-zinc-400" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 1.5 }}
                  className="p-4 rounded-full bg-amber-400/10 border-2 border-amber-400/30"
                >
                  <p className="text-3xl font-bold text-amber-300">
                    +<AnimatedCounter from={0} to={scoreChange} delay={1.6} />
                  </p>
                  <p className="text-xs font-semibold tracking-wider text-amber-400/70 -mt-1">Point Improvement</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
                >
                  <p className="text-xs font-semibold tracking-widest text-amber-400">After</p>
                  <AnimatedCounter from={scoreBefore} to={scoreAfter} delay={0.6} className="text-4xl font-bold text-amber-400" />
                </motion.div>
              </div>

              {/* Image Comparison */}
              <motion.div
                className="flex gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7, ease: 'easeOut' }}
              >
                <div className="w-1/2">
                  <img src={originalImageUrl} alt="Before" className="w-full object-cover rounded-lg border-2 border-zinc-700" />
                  <p className="text-center text-xs font-semibold tracking-widest text-zinc-500 mt-2">Before</p>
                </div>
                <div className="w-1/2">
                  <img src={generatedImageUrl} alt="After" className="w-full object-cover rounded-lg border-2 border-amber-400/50" />
                  <p className="text-center text-xs font-semibold tracking-widest text-amber-400 mt-2">After</p>
                </div>
              </motion.div>

              {/* Metric Breakdown */}
              <div className="space-y-4 pt-4 border-t border-amber-400/10">
                <motion.h3
                  className="text-sm font-semibold text-center text-zinc-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.1, ease: 'easeOut' }}
                >
                  Score Breakdown
                </motion.h3>
                {transformationAnalysis.comparativeMetrics.map((metric, index) => (
                  <MetricBar
                    key={metric.area}
                    label={metric.area}
                    beforeScore={metric.beforeScore}
                    afterScore={metric.afterScore}
                    delay={index * 0.15}
                  />
                ))}
              </div>

            </div>

            {/* Footer Action */}
            <motion.footer
              className="flex-shrink-0 p-4 border-t border-amber-400/10"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.8, ease: 'easeOut' }}
            >
              <motion.button
                onClick={handleShare}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold bg-amber-400 text-black transition-all duration-300 hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-amber-400"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ShareIcon className="w-5 h-5" />
                Share Results
              </motion.button>
            </motion.footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrestigeModal;
